import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, User, Mail, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import styled from 'styled-components';
import { Layout } from '../components/ui/Layout';
import { 
  Form, 
  FormGroup, 
  Label, 
  Input, 
  Button, 
  ErrorMessage, 
  Divider, 
  DividerText 
} from '../components/ui/Form';
import { theme } from '../styles/theme';
import { authService } from '../services/authService';
import { RegisterRequest } from '../types/auth';

const InputContainer = styled.div`
  position: relative;
`;

const InputIcon = styled.div`
  position: absolute;
  left: ${theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.textTertiary};
  z-index: 1;
`;

const StyledInput = styled(Input)`
  padding-left: ${theme.spacing['3xl']};
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: ${theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${theme.colors.textTertiary};
  cursor: pointer;
  padding: ${theme.spacing.xs};
  border-radius: ${theme.borderRadius.sm};
  
  &:hover {
    color: ${theme.colors.textPrimary};
    background-color: ${theme.colors.gray50};
  }
`;

const PasswordStrength = styled.div<{ $strength: number }>`
  height: 4px;
  background-color: ${theme.colors.gray200};
  border-radius: ${theme.borderRadius.full};
  margin-top: ${theme.spacing.sm};
  overflow: hidden;
  
  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${({ $strength }) => ($strength / 4) * 100}%;
    background-color: ${({ $strength }) => {
      if ($strength <= 1) return theme.colors.error;
      if ($strength <= 2) return theme.colors.warning;
      if ($strength <= 3) return theme.colors.info;
      return theme.colors.success;
    }};
    transition: all ${theme.transitions.normal};
  }
`;

const PasswordStrengthText = styled.div<{ $strength: number }>`
  font-size: ${theme.fontSizes.xs};
  margin-top: ${theme.spacing.xs};
  color: ${({ $strength }) => {
    if ($strength <= 1) return theme.colors.error;
    if ($strength <= 2) return theme.colors.warning;
    if ($strength <= 3) return theme.colors.info;
    return theme.colors.success;
  }};
`;

const TermsContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing.sm};
  margin: ${theme.spacing.md} 0;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  accent-color: ${theme.colors.primary};
  margin-top: 2px;
`;

const TermsText = styled.label`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.textSecondary};
  line-height: 1.4;
  cursor: pointer;
`;

const TermsLink = styled.a`
  color: ${theme.colors.primary};
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const SignInPrompt = styled.div`
  text-align: center;
  margin-top: ${theme.spacing.lg};
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.textSecondary};
`;

const SignInLink = styled(Link)`
  color: ${theme.colors.primary};
  font-weight: ${theme.fontWeights.medium};
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

interface RegisterFormData extends RegisterRequest {
  acceptTerms: boolean;
}

const validationSchema: yup.ObjectSchema<RegisterFormData> = yup.object({
  username: yup
    .string()
    .required('Username is required')
    .min(3, 'Username must be at least 3 characters')
    .max(20, 'Username must be less than 20 characters')
    .matches(/^[a-zA-Z0-9_]+$/, 'Username can only contain letters, numbers, and underscores'),
  email: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email address'),
  password: yup
    .string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters')
    .matches(/(?=.*[a-z])/, 'Password must contain at least one lowercase letter')
    .matches(/(?=.*[A-Z])/, 'Password must contain at least one uppercase letter')
    .matches(/(?=.*\d)/, 'Password must contain at least one number'),
  confirmPassword: yup
    .string()
    .required('Please confirm your password')
    .oneOf([yup.ref('password')], 'Passwords must match'),
  acceptTerms: yup
    .boolean()
    .required()
    .oneOf([true], 'You must accept the terms and conditions'),
});

const calculatePasswordStrength = (password: string): number => {
  let strength = 0;
  if (password.length >= 6) strength++;
  if (/(?=.*[a-z])/.test(password)) strength++;
  if (/(?=.*[A-Z])/.test(password)) strength++;
  if (/(?=.*\d)/.test(password)) strength++;
  if (/(?=.*[!@#$%^&*])/.test(password)) strength++;
  return Math.min(strength, 4);
};

const getPasswordStrengthText = (strength: number): string => {
  if (strength <= 1) return 'Weak';
  if (strength <= 2) return 'Fair';
  if (strength <= 3) return 'Good';
  return 'Strong';
};

export const Register: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setError,
  } = useForm<RegisterFormData>({
    resolver: yupResolver(validationSchema),
  });

  const password = watch('password');

  React.useEffect(() => {
    if (password) {
      setPasswordStrength(calculatePasswordStrength(password));
    } else {
      setPasswordStrength(0);
    }
  }, [password]);

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    
    try {
      const response = await authService.register(data);
      
      if (response.success) {
        toast.success('Registration successful! Please sign in to continue.');
        navigate('/login');
      } else {
        if (response.error?.includes('username')) {
          setError('username', { message: response.error });
        } else if (response.error?.includes('email')) {
          setError('email', { message: response.error });
        } else {
          toast.error(response.error || 'Registration failed');
        }
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout
      title="Create Account"
      subtitle="Join AllMed AI and start your journey"
    >
      <Form onSubmit={handleSubmit(onSubmit)}>
        <FormGroup>
          <Label htmlFor="username">Username</Label>
          <InputContainer>
            <InputIcon>
              <User size={20} />
            </InputIcon>
            <StyledInput
              id="username"
              type="text"
              placeholder="Choose a username"
              $hasError={!!errors.username}
              {...register('username')}
            />
          </InputContainer>
          {errors.username && (
            <ErrorMessage>
              <AlertCircle size={16} />
              {errors.username.message}
            </ErrorMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="email">Email Address</Label>
          <InputContainer>
            <InputIcon>
              <Mail size={20} />
            </InputIcon>
            <StyledInput
              id="email"
              type="email"
              placeholder="Enter your email"
              $hasError={!!errors.email}
              {...register('email')}
            />
          </InputContainer>
          {errors.email && (
            <ErrorMessage>
              <AlertCircle size={16} />
              {errors.email.message}
            </ErrorMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="password">Password</Label>
          <InputContainer>
            <InputIcon>
              <Lock size={20} />
            </InputIcon>
            <StyledInput
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Create a strong password"
              $hasError={!!errors.password}
              {...register('password')}
            />
            <PasswordToggle
              type="button"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </PasswordToggle>
          </InputContainer>
          {password && (
            <>
              <PasswordStrength $strength={passwordStrength} />
              <PasswordStrengthText $strength={passwordStrength}>
                Password strength: {getPasswordStrengthText(passwordStrength)}
              </PasswordStrengthText>
            </>
          )}
          {errors.password && (
            <ErrorMessage>
              <AlertCircle size={16} />
              {errors.password.message}
            </ErrorMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="confirmPassword">Confirm Password</Label>
          <InputContainer>
            <InputIcon>
              <Lock size={20} />
            </InputIcon>
            <StyledInput
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm your password"
              $hasError={!!errors.confirmPassword}
              {...register('confirmPassword')}
            />
            <PasswordToggle
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </PasswordToggle>
          </InputContainer>
          {errors.confirmPassword && (
            <ErrorMessage>
              <AlertCircle size={16} />
              {errors.confirmPassword.message}
            </ErrorMessage>
          )}
        </FormGroup>

        <TermsContainer>
          <Checkbox 
            type="checkbox" 
            id="acceptTerms"
            {...register('acceptTerms')}
          />
          <TermsText htmlFor="acceptTerms">
            I agree to the{' '}
            <TermsLink href="#" target="_blank" rel="noopener noreferrer">
              Terms of Service
            </TermsLink>{' '}
            and{' '}
            <TermsLink href="#" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </TermsLink>
          </TermsText>
        </TermsContainer>
        {errors.acceptTerms && (
          <ErrorMessage>
            <AlertCircle size={16} />
            {errors.acceptTerms.message}
          </ErrorMessage>
        )}

        <Button 
          type="submit" 
          $fullWidth 
          $loading={isLoading}
          disabled={isLoading}
        >
          {isLoading ? 'Creating account...' : 'Create Account'}
        </Button>

        <Divider>
          <DividerText>or</DividerText>
        </Divider>

        <SignInPrompt>
          Already have an account?{' '}
          <SignInLink to="/login">Sign in</SignInLink>
        </SignInPrompt>
      </Form>
    </Layout>
  );
};